import React from 'react';
import { Phone, FileText, AlertTriangle, CheckCircle, BookOpen, Shield } from 'lucide-react';
export function DotInspectionPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-5240">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-5241">
        <div className="container mx-auto px-4" data-id="element-5242">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-5243">
            DOT Inspection
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-5244">
            Federal Motor Carrier Safety Regulations
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-5245">
        <div className="max-w-4xl mx-auto" data-id="element-5246">
          {/* Disclaimer */}
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8 text-sm" data-id="element-5247">
            <p className="text-red-900" data-id="element-5248">
              Although we make every effort to assure that the information we
              provide is complete and accurate, it is not intended to take the
              place of published agency regulations. Regulations issued by the
              U.S. Department of Transportation and its Operating
              Administrations are published in the Federal Register and compiled
              in the U.S. Code of Federal Regulations (CFR). Copies of
              appropriate volumes of the CFR in book format may be purchased
              from the Superintendent of Documents,{' '}
              <a href="http://www.gpoaccess.gov/" target="_blank" rel="noopener noreferrer" className="text-red-700 hover:underline font-semibold" data-id="element-5249">
                U.S. Government Printing Office
              </a>
              , examined at many libraries.
            </p>
          </div>

          {/* General Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5250">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5251">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-5252" />
              General Requirements
            </h2>
            <p className="text-lg text-gray-700" data-id="element-5253">
              <strong data-id="element-5254">General —</strong> Every motor carrier, its officers,
              drivers, agents, representatives, and employees directly concerned
              with the inspection or maintenance of motor vehicles shall comply
              and be conversant with the rules of this part.
            </p>
            <p className="text-sm text-gray-600 mt-4" data-id="element-5255">
              [44 FR 38526, July 2, 1979, as amended at 53 FR 18058, May 19,
              1988]
            </p>
          </div>

          {/* §396.3 Inspection, Repair and Maintenance */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5256">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5257">
              §396.3 Inspection, Repair and Maintenance
            </h2>

            <div className="space-y-6" data-id="element-5258">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5259">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5260">(a) General</h3>
                <p className="text-gray-700 mb-3" data-id="element-5261">
                  Every motor carrier shall systematically inspect, repair, and
                  maintain, or cause to be systematically inspected, repaired,
                  and maintained, all motor vehicles subject to its control.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-5262">
                  <li data-id="element-5263">
                    <strong data-id="element-5264">(a)(1)</strong> Parts and accessories shall be in
                    safe and proper operating condition at all times. These
                    include those specified in Part 393 and any additional parts
                    and accessories which may affect safety of operation,
                    including but not limited to, frame and frame assemblies,
                    suspension systems, axles and attaching parts, wheels and
                    rims, and steering systems.
                  </li>
                  <li data-id="element-5265">
                    <strong data-id="element-5266">(a)(2)</strong> Pushout windows, emergency doors,
                    and emergency door marking lights in buses shall be
                    inspected at least every 90 days.
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-5267">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5268">
                  (b) Required Records
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-5269">
                  For vehicles controlled for 30 consecutive days or more,
                  except for a private motor carrier of passengers
                  (nonbusiness), the motor carriers shall maintain, or cause to
                  be maintained, the following record for each vehicle:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4" data-id="element-5270">
                  <li data-id="element-5271">
                    <strong data-id="element-5272">(b)(1)</strong> An identification of the vehicle
                    including company number, make, serial number, year, and
                    tire size
                  </li>
                  <li data-id="element-5273">
                    <strong data-id="element-5274">(b)(2)</strong> A means to indicate the nature and
                    due date of various inspection and maintenance operations
                  </li>
                  <li data-id="element-5275">
                    <strong data-id="element-5276">(b)(3)</strong> A record of inspection, repairs and
                    maintenance indicating their date and nature
                  </li>
                  <li data-id="element-5277">
                    <strong data-id="element-5278">(b)(4)</strong> A record of tests conducted on
                    pushout windows, emergency doors, and emergency door marking
                    lights on buses
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-5279">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5280">
                  (c) Record Retention
                </h3>
                <p className="text-gray-700" data-id="element-5281">
                  The records required by this section shall be retained where
                  the vehicle is either housed or maintained for a period of 1
                  year and for 6 months after the motor vehicle leaves the motor
                  carrier's control.
                </p>
              </div>
            </div>
          </div>

          {/* §396.5 Lubrication */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5282">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5283">
              §396.5 Lubrication
            </h2>
            <p className="text-gray-700 mb-4" data-id="element-5284">
              Every motor carrier shall ensure that each motor vehicle subject
              to its control is—
            </p>
            <ul className="space-y-2 text-gray-700" data-id="element-5285">
              <li className="flex items-start" data-id="element-5286">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5287" />
                <span data-id="element-5288">
                  <strong data-id="element-5289">(a)</strong> properly lubricated; and
                </span>
              </li>
              <li className="flex items-start" data-id="element-5290">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5291" />
                <span data-id="element-5292">
                  <strong data-id="element-5293">(b)</strong> free of oil and grease leaks.
                </span>
              </li>
            </ul>
          </div>

          {/* §396.7 Unsafe Operations Forbidden */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-5294">
            <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center" data-id="element-5295">
              <AlertTriangle className="mr-3" size={28} data-id="element-5296" />
              §396.7 Unsafe Operations Forbidden
            </h2>
            <div className="space-y-4" data-id="element-5297">
              <div data-id="element-5298">
                <p className="text-gray-800 mb-2" data-id="element-5299">
                  <strong data-id="element-5300">(a) General —</strong>
                </p>
                <p className="text-gray-700" data-id="element-5301">
                  A motor vehicle shall not be operated in such a condition as
                  to likely cause an accident or a breakdown of the vehicle.
                </p>
              </div>
              <div data-id="element-5302">
                <p className="text-gray-800 mb-2" data-id="element-5303">
                  <strong data-id="element-5304">(b) Exemption —</strong>
                </p>
                <p className="text-gray-700" data-id="element-5305">
                  Any motor vehicle discovered to be in an unsafe condition
                  while being operated on the highway may be continued in
                  operation only to the nearest place where repairs can safely
                  be effected. Such operation shall be conducted only if it is
                  less hazardous to the public than to permit the vehicle to
                  remain on the highway.
                </p>
              </div>
            </div>
          </div>

          {/* §396.9 Inspection of Motor Vehicles in Operation */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5306">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5307">
              §396.9 Inspection of Motor Vehicles in Operation
            </h2>

            <div className="space-y-6" data-id="element-5308">
              <div data-id="element-5309">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5310">
                  (a) Personnel Authorized to Perform Inspections
                </h3>
                <p className="text-gray-700" data-id="element-5311">
                  Every special agent of the FMCSA is authorized to enter upon
                  and perform inspections of motor carrier's vehicles in
                  operation.
                </p>
              </div>

              <div data-id="element-5312">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5313">
                  (b) Prescribed Inspection Report
                </h3>
                <p className="text-gray-700" data-id="element-5314">
                  The Driver Vehicle Examination Report shall be used to record
                  results of motor vehicle inspections conducted by authorized
                  FMCSA personnel.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-5315">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5316">
                  (c) Motor Vehicles Declared "Out of Service"
                </h3>
                <ul className="space-y-3 text-gray-700" data-id="element-5317">
                  <li data-id="element-5318">
                    <strong data-id="element-5319">(c)(1)</strong> Authorized personnel shall declare
                    and mark "out of service" any motor vehicle which by reason
                    of its mechanical condition or loading would likely cause an
                    accident or a breakdown.
                  </li>
                  <li data-id="element-5320">
                    <strong data-id="element-5321">(c)(2)</strong> No motor carrier shall require or
                    permit any person to operate nor shall any person operate
                    any motor vehicle declared and marked "out of service" until
                    all repairs required by the "out of service notice" have
                    been satisfactorily completed.
                  </li>
                  <li data-id="element-5322">
                    <strong data-id="element-5323">(c)(3)</strong> No person shall remove the "Out of
                    Service Vehicle" sticker from any motor vehicle prior to
                    completion of all repairs required by the "out of service
                    notice".
                  </li>
                </ul>
              </div>

              <div data-id="element-5324">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5325">
                  (d) Motor Carrier Disposition
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5326">
                  <li data-id="element-5327">
                    <strong data-id="element-5328">(d)(1)</strong> The driver shall deliver the
                    inspection report to the motor carrier upon arrival at the
                    next terminal or facility, or mail it immediately if not
                    scheduled to arrive within 24 hours.
                  </li>
                  <li data-id="element-5329">
                    <strong data-id="element-5330">(d)(2)</strong> Motor carriers shall examine the
                    report. Violations or defects noted thereon shall be
                    corrected.
                  </li>
                  <li data-id="element-5331">
                    <strong data-id="element-5332">(d)(3)</strong> Within 15 days following the date of
                    inspection, the motor carrier shall certify that all
                    violations noted have been corrected and return the
                    completed form to the issuing agency.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* §396.11 Driver Vehicle Inspection Report */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5333">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5334">
              §396.11 Driver Vehicle Inspection Report(s)
            </h2>

            <div className="space-y-6" data-id="element-5335">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5336">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5337">
                  (a) Report Required
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-5338">
                  Every motor carrier shall require its drivers to report, and
                  every driver shall prepare a report in writing at the
                  completion of each day's work on each vehicle operated. The
                  report shall cover at least the following parts and
                  accessories:
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-gray-700" data-id="element-5339">
                  <div className="flex items-start" data-id="element-5340">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5341" />
                    <span data-id="element-5342">
                      Service brakes including trailer brake connections
                    </span>
                  </div>
                  <div className="flex items-start" data-id="element-5343">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5344" />
                    <span data-id="element-5345">Parking (hand) brake</span>
                  </div>
                  <div className="flex items-start" data-id="element-5346">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5347" />
                    <span data-id="element-5348">Steering mechanism</span>
                  </div>
                  <div className="flex items-start" data-id="element-5349">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5350" />
                    <span data-id="element-5351">Lighting devices and reflectors</span>
                  </div>
                  <div className="flex items-start" data-id="element-5352">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5353" />
                    <span data-id="element-5354">Tires</span>
                  </div>
                  <div className="flex items-start" data-id="element-5355">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5356" />
                    <span data-id="element-5357">Horn</span>
                  </div>
                  <div className="flex items-start" data-id="element-5358">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5359" />
                    <span data-id="element-5360">Windshield wipers</span>
                  </div>
                  <div className="flex items-start" data-id="element-5361">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5362" />
                    <span data-id="element-5363">Rear vision mirrors</span>
                  </div>
                  <div className="flex items-start" data-id="element-5364">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5365" />
                    <span data-id="element-5366">Coupling devices</span>
                  </div>
                  <div className="flex items-start" data-id="element-5367">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5368" />
                    <span data-id="element-5369">Wheels and rims</span>
                  </div>
                  <div className="flex items-start" data-id="element-5370">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5371" />
                    <span data-id="element-5372">Emergency equipment</span>
                  </div>
                </div>
              </div>

              <div data-id="element-5373">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5374">
                  (b) Report Content
                </h3>
                <p className="text-gray-700" data-id="element-5375">
                  The report shall identify the vehicle and list any defect or
                  deficiency discovered. If no defect or deficiency is
                  discovered, the report shall so indicate. The driver shall
                  sign the report.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-5376">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5377">
                  (c) Corrective Action
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5378">
                  <li data-id="element-5379">
                    <strong data-id="element-5380">(c)(1)</strong> Every motor carrier shall certify on
                    the original driver vehicle inspection report that the
                    defect or deficiency has been repaired or that repair is
                    unnecessary before the vehicle is operated again.
                  </li>
                  <li data-id="element-5381">
                    <strong data-id="element-5382">(c)(2)</strong> Every motor carrier shall maintain
                    the original driver vehicle inspection report, the
                    certification of repairs, and the certification of the
                    driver's review for three months from the date the written
                    report was prepared.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* §396.13 Driver Inspection */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5383">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5384">
              §396.13 Driver Inspection
            </h2>
            <p className="text-gray-700 mb-4" data-id="element-5385">
              Before driving a motor vehicle, the driver shall:
            </p>
            <ul className="space-y-2 text-gray-700" data-id="element-5386">
              <li className="flex items-start" data-id="element-5387">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5388" />
                <span data-id="element-5389">
                  <strong data-id="element-5390">(a)</strong> Be satisfied that the motor vehicle is in
                  safe operating condition;
                </span>
              </li>
              <li className="flex items-start" data-id="element-5391">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5392" />
                <span data-id="element-5393">
                  <strong data-id="element-5394">(b)</strong> Review the last driver vehicle inspection
                  report; and
                </span>
              </li>
              <li className="flex items-start" data-id="element-5395">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5396" />
                <span data-id="element-5397">
                  <strong data-id="element-5398">(c)</strong> Sign the report, only if defects or
                  deficiencies were noted, to acknowledge review and
                  certification that required repairs have been performed.
                </span>
              </li>
            </ul>
          </div>

          {/* §396.17 Periodic Inspection */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5399">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-5400">
              <Shield className="mr-3" size={32} data-id="element-5401" />
              §396.17 Periodic Inspection
            </h2>

            <div className="space-y-4" data-id="element-5402">
              <div className="bg-white/10 rounded-lg p-4" data-id="element-5403">
                <p className="mb-2" data-id="element-5404">
                  <strong data-id="element-5405">(a)</strong> Every commercial motor vehicle shall be
                  inspected as required by this section. The inspection shall
                  include, at a minimum, the parts and accessories set forth in
                  Appendix G.
                </p>
                <p className="text-sm text-gray-200" data-id="element-5406">
                  Note: The term commercial motor vehicle includes each vehicle
                  in a combination vehicle.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-5407">
                <p data-id="element-5408">
                  <strong data-id="element-5409">(b)</strong> A motor carrier shall inspect or cause to
                  be inspected all motor vehicles subject to its control.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-5410">
                <p className="mb-2" data-id="element-5411">
                  <strong data-id="element-5412">(c)</strong> A motor carrier shall not use a
                  commercial motor vehicle unless each component has passed an
                  inspection at least once during the preceding 12 months and
                  documentation is on the vehicle.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-5413">
                <p data-id="element-5414">
                  <strong data-id="element-5415">(f)</strong> Vehicles passing roadside or periodic
                  inspections performed under State or FMCSA auspices will be
                  considered to have met annual inspection requirements for 12
                  months.
                </p>
              </div>
            </div>
          </div>

          {/* §396.19 Inspector Qualifications */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5416">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5417">
              §396.19 Inspector Qualifications
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4" data-id="element-5418">
              <p className="text-gray-700 mb-3" data-id="element-5419">
                The motor carrier shall ensure that individuals performing
                annual inspections are qualified as follows:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-5420">
                <li data-id="element-5421">
                  <strong data-id="element-5422">(a)(1)</strong> Understands the inspection criteria
                  set forth in 49 CFR Part 393 and Appendix G and can identify
                  defective components
                </li>
                <li data-id="element-5423">
                  <strong data-id="element-5424">(a)(2)</strong> Is knowledgeable of and has mastered
                  the methods, procedures, tools and equipment used when
                  performing an inspection
                </li>
                <li data-id="element-5425">
                  <strong data-id="element-5426">(a)(3)</strong> Is capable of performing an inspection
                  by reason of experience, training, or both
                </li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6" data-id="element-5427">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-5428">
                Qualifying Training/Experience:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-5429">
                <li data-id="element-5430">
                  • Successfully completed State or Federal sponsored training
                  program, OR
                </li>
                <li data-id="element-5431">• Has certificate from State or Canadian Province, OR</li>
                <li data-id="element-5432">
                  • Has combination of training and/or experience totaling at
                  least 1 year
                </li>
              </ul>
            </div>
          </div>

          {/* §396.25 Brake Inspector Qualifications */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5433">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5434">
              §396.25 Qualifications of Brake Inspectors
            </h2>

            <div className="space-y-4" data-id="element-5435">
              <p className="text-gray-700" data-id="element-5436">
                <strong data-id="element-5437">(a)</strong> The motor carrier shall ensure that all
                inspections, maintenance, repairs or service to the brakes of
                its commercial motor vehicles are performed in compliance with
                the requirements of this section.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-5438">
                <p className="text-gray-800 mb-3" data-id="element-5439">
                  <strong data-id="element-5440">(d)</strong> The motor carrier shall ensure that each
                  brake inspector is qualified as follows:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-5441">
                  <li data-id="element-5442">
                    <strong data-id="element-5443">(d)(1)</strong> Understands the brake service or
                    inspection task and can perform it
                  </li>
                  <li data-id="element-5444">
                    <strong data-id="element-5445">(d)(2)</strong> Is knowledgeable of methods,
                    procedures, tools and equipment
                  </li>
                  <li data-id="element-5446">
                    <strong data-id="element-5447">(d)(3)</strong> Is capable by reason of experience,
                    training or both (at least 1 year combined)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* DOT Inspection Form Sample */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5448">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5449">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-5450" />
              DOT Inspection Form Sample
            </h2>
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden" data-id="element-5451">
              <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/preview/uploaded/images/DOT%20INSPECTION%20FORM%20sample.JPG?opt=w3" alt="DOT Inspection Form Sample" className="w-full h-auto" data-id="element-5452" />
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5453">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5454">Key Takeaways</h2>
            <div className="space-y-3" data-id="element-5455">
              <div className="flex items-start" data-id="element-5456">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5457" />
                <span data-id="element-5458">
                  Annual inspections required for all commercial motor vehicles
                </span>
              </div>
              <div className="flex items-start" data-id="element-5459">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5460" />
                <span data-id="element-5461">Daily driver vehicle inspection reports mandatory</span>
              </div>
              <div className="flex items-start" data-id="element-5462">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5463" />
                <span data-id="element-5464">
                  Qualified inspectors must meet specific training/experience
                  requirements
                </span>
              </div>
              <div className="flex items-start" data-id="element-5465">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5466" />
                <span data-id="element-5467">Records must be maintained for specified periods</span>
              </div>
              <div className="flex items-start" data-id="element-5468">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5469" />
                <span data-id="element-5470">
                  Unsafe vehicles cannot be operated until repairs are completed
                </span>
              </div>
              <div className="flex items-start" data-id="element-5471">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5472" />
                <span data-id="element-5473">Brake inspectors require specialized qualifications</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-5474">
            <h2 className="text-3xl font-bold mb-4" data-id="element-5475">
              Need DOT Inspection Services?
            </h2>
            <p className="text-xl mb-6" data-id="element-5476">
              Certified inspectors ensuring compliance with all federal
              regulations
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-5477">
              <Phone className="mr-3" size={24} data-id="element-5478" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-5479">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}